import { AdminRole } from "@repo/api-dtos"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { X } from "lucide-react"
import * as React from "react"
import { PageHeader } from "@/components/layout"
import { QuickFilter } from "@/components/promotions/grid-view-actions"
import { ToolbarSearch } from "@/components/promotions/toolbar-search"
import { Button } from "@/components/ui/button"
import { DebouncedSearchInput } from "@/features/admin-grid/DebouncedSearchInput"
import { ReferralsAdminGrid } from "@/features/referrals-admin/ReferralsAdminGrid"
import type { ReferralsAdminInput } from "@/features/referrals-admin/referrals-admin-datasource"
import { useAdminRoles } from "@/hooks/use-admin-roles"

const MAX_SEARCH_LENGTH = 100

/** URL search params of the referrals list. */
export interface ReferralsSearch {
	page?: number
	search?: string
	status?: string
	flagged?: string
	pending?: string
}

export const Route = createFileRoute("/referrals/")({
	component: ReferralsPage,
	validateSearch: (search: Record<string, unknown>): ReferralsSearch => ({
		page: normalizePage(search.page),
		search: normalizeSearch(search.search),
		status: search.status as string | undefined,
		flagged: search.flagged === "1" ? "1" : undefined,
		pending: search.pending === "1" ? "1" : undefined,
	}),
})

function normalizePage(raw: unknown): number | undefined {
	const n = Number(raw)
	if (!Number.isFinite(n)) {
		return undefined
	}
	const p = Math.floor(n)
	return p > 0 ? p : undefined
}

function normalizeSearch(raw: unknown): string | undefined {
	if (typeof raw !== "string") {
		return undefined
	}
	return raw.trim().slice(0, MAX_SEARCH_LENGTH) || undefined
}

function ReferralsPage() {
	const searchParams = useSearch({ from: "/referrals/" })
	const navigate = useNavigate()
	const { hasRole } = useAdminRoles()
	const canReview = hasRole(AdminRole.MarketplaceCoupons)
	const canSuspend = hasRole(AdminRole.MarketplaceUserManagement)

	const pageIndex = (searchParams.page ?? 1) - 1
	const searchRef = React.useRef<{ clear: () => void }>(null)

	const updateFilters = React.useCallback(
		(updates: Record<string, string | number | undefined>) => {
			navigate({
				to: "/referrals",
				search: (prev: Record<string, unknown>) => {
					const next = { ...prev, ...updates }
					for (const key of Object.keys(next)) {
						const val = next[key as keyof typeof next]
						if (val === undefined || val === "") {
							delete next[key as keyof typeof next]
						}
					}
					return next as never
				},
				replace: true,
			})
		},
		[navigate],
	)

	const handleDebouncedSearch = React.useCallback(
		(value: string) => {
			updateFilters({ search: normalizeSearch(value), page: undefined })
		},
		[updateFilters],
	)

	const gridInput = React.useMemo<ReferralsAdminInput>(
		() => ({
			search: searchParams.search,
			flagged: searchParams.flagged === "1" ? true : undefined,
			pendingOnly: searchParams.pending === "1" ? true : undefined,
		}),
		[searchParams.search, searchParams.flagged, searchParams.pending],
	)

	const handlePageChange = React.useCallback(
		(nextPageIndex: number) => {
			updateFilters({
				page: nextPageIndex > 0 ? nextPageIndex + 1 : undefined,
			})
		},
		[updateFilters],
	)

	const clearFilters = React.useCallback(() => {
		searchRef.current?.clear()
		navigate({ to: "/referrals", search: {} as never, replace: true })
	}, [navigate])

	const hasActiveFilters =
		!!searchParams.status ||
		!!searchParams.search ||
		!!searchParams.flagged ||
		!!searchParams.pending

	const toggleChip = React.useCallback(
		(key: "flagged" | "pending") => {
			updateFilters({
				[key]: searchParams[key] === "1" ? undefined : "1",
				page: undefined,
			})
		},
		[searchParams, updateFilters],
	)

	const toolbar = (
		<>
			<ToolbarSearch>
				<DebouncedSearchInput
					defaultValue={searchParams.search ?? ""}
					onDebouncedChange={handleDebouncedSearch}
					placeholder="Code, username or email"
					inputRef={searchRef}
					maxLength={MAX_SEARCH_LENGTH}
				/>
			</ToolbarSearch>

			<div className="flex items-center gap-1">
				<QuickFilter
					active={searchParams.flagged === "1"}
					onToggle={() => toggleChip("flagged")}
				>
					Flagged
				</QuickFilter>
				<QuickFilter
					active={searchParams.pending === "1"}
					onToggle={() => toggleChip("pending")}
				>
					Pending release
				</QuickFilter>
			</div>

			{hasActiveFilters && (
				<Button variant="ghost" onClick={clearFilters}>
					<X />
					Clear
				</Button>
			)}
		</>
	)

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-6 p-6">
			<PageHeader
				title="Referrals"
				description="Every checkout paid with a referral code: the discount it took, the reward it earns and the flags to look at before a batch is released"
			/>

			<div className="min-h-0 flex-1">
				<ReferralsAdminGrid
					input={gridInput}
					pageIndex={pageIndex}
					onPageChange={handlePageChange}
					toolbar={toolbar}
					canReview={canReview}
					canSuspend={canSuspend}
				/>
			</div>
		</div>
	)
}
